/**
 * oneTimeModals — at most one "here's what's new" dialog per page load.
 *
 * Three of these now exist (founder message, Pro welcome, tutorial prompt) and
 * they mount in two different trees, each deciding for itself whether to open.
 * Nothing stopped two from deciding yes on the same login, and two stacked
 * dialogs on arrival is worse than showing neither: the one underneath is
 * invisible, so it is dismissed unread and never returns.
 *
 * Deliberately per PAGE LOAD, not persisted. A modal that loses the race is not
 * marked seen, so it simply comes back next login — which is the behaviour you
 * want from "at most one", rather than silently burning someone's only chance
 * to read it.
 */

/** Lower number wins. Ties go to whoever claimed first. */
export const MODAL_PRIORITY = {
  proWelcome: 0,      // paid for something 30 seconds ago; most time-critical
  founderMessage: 1,
  tutorialPrompt: 2,  // useful whenever it arrives — always yields
} as const;

export type OneTimeModalId = keyof typeof MODAL_PRIORITY;

let claimed: OneTimeModalId | null = null;

/**
 * Ask to be the modal this session.
 *
 * A higher-priority claimant may take the slot from a lower one that has not
 * been shown — the founder message and the Pro welcome are gated on async
 * profile and subscription state, so which one asks first is a matter of
 * network timing, not intent.
 */
export function claimOneTimeModal(id: OneTimeModalId): boolean {
  if (claimed === null) { claimed = id; return true; }
  if (claimed === id) return true;
  if (MODAL_PRIORITY[id] < MODAL_PRIORITY[claimed]) { claimed = id; return true; }
  return false;
}

/** Did this modal keep the slot? False once something higher took it. */
export const holdsOneTimeModal = (id: OneTimeModalId): boolean => claimed === id;

/** Test seam only — a page load is the real reset. */
export function __resetOneTimeModals(): void { claimed = null; }
